import type {
  PieceShape,
  PieceStyle,
  PieceColorTheme,
  KingIcon,
  CrownType,
  Material,
} from '@draughts/shared';
import { CROWN_TYPES, GameVariant, VARIANT_SPECS } from '@draughts/shared';
// Solo il catalogo dei tier: piece-lod.ts non importa nulla da qui, quindi non c'è ciclo.
import { LOD_SPEC, LOD_TIERS, TIER_MIN_DIAMETER_PX } from './piece-lod';
// La luminanza WCAG è una primitiva di colore e vive accanto alle conversioni: qui
// serve al contrasto dell'emblema, ma la stessa soglia governa anche la casella scura
// e lo spazio dei colori personalizzati.
import { relativeLuminance } from '../../utils/color-oklch';

export interface GradientStop {
  offset: string;
  color: string;
}

export interface ColorPair {
  white: GradientStop[];
  dark: GradientStop[];
  whiteStroke: string;
  darkStroke: string;
  whiteCrown: string;
  darkCrown: string;
  whiteCrownStroke: string;
  darkCrownStroke: string;
}

/**
 * Geometria in rilievo di uno stile 3D. Gli stili che NON hanno questo campo
 * percorrono esattamente l'albero SVG piatto di sempre.
 *
 * Tutte le misure sono frazioni di `radius`.
 */
export interface ReliefSpec {
  /**
   * Altezza del cilindro (lo spessore del pezzo), frazione di `radius`. Sugli stili
   * impilati la dama è due dischi, quindi circa il doppio della pedina con una
   * giuntura a metà; su `3d-crown` il corpo non cresce e il volume lo aggiunge la corona.
   *
   * VINCOLO DURO sull'altezza totale. Il pezzo è disegnato con `size = 0.8 * SQUARE_SIZE`,
   * quindi `radius = 0.4 * SQUARE_SIZE` e la casella offre `1.25 * radius` di semi-altezza.
   * La silhouette si estende per `FACE_SQUASH + (kingHeight + crownHeight) / 2` sopra e
   * sotto il centro, e la scacchiera ha `overflow: hidden` sul contenitore
   * (GameScreen.css): superata quella soglia, la dama dell'ultima riga viene TAGLIATA su
   * ogni scacchiera. L'invariante è verificata da piece-themes.test.ts.
   */
  manHeight: number;
  kingHeight: number;
}

/**
 * MATERIALE: come il pezzo prende la luce e che venatura ha. Non tocca la geometria.
 */
export interface MaterialSpec {
  // Radial gradient center & radius (percent strings, fed to <radialGradient>)
  cx: string;
  cy: string;
  r: string;
  // Inner-light highlight stops
  innerLightWhite: [string, string];
  innerLightDark: [string, string];
  // Inner-shadow stops at the bottom
  innerDarkWhite: [string, string];
  innerDarkDark: [string, string];
  // Drop-shadow opacity (per side)
  shadowOpacityWhite: number;
  shadowOpacityDark: number;
  /**
   * Pattern di venatura da sovrapporre alla faccia, o `null` per i materiali senza
   * venatura. È l'id di un pattern dichiarato una volta sola da MaterialDefs.
   */
  grain: GrainKind | null;
  /** Opacità dell'overlay di venatura: è ciò che distingue legno (marcata) da marmo (lieve). */
  grainOpacity: number;
  /** Contrasto dell'incisione: 'soft' = legno tornito, 'crisp' = disco da torneo. */
  cut: 'soft' | 'crisp';
  /** Corpo semitrasparente (vetro): il nucleo centrale resta opaco per non perdere contrasto. */
  translucent?: boolean;
}

/** Le venature disponibili; il vetro non ne ha, ma ha un riflesso speculare. */
export type GrainKind = 'wood' | 'marble';

/**
 * GEOMETRIA: cosa si disegna. Ortogonale al materiale, che decide di che colore.
 */
export interface StyleGeometry {
  /** Assente ⇒ stile piatto: niente parete, niente giuntura, niente corona 3D. */
  relief?: ReliefSpec;
  /**
   * Raggi dei solchi concentrici, frazione di `geom.rx`, dal più esterno; `[]` = faccia
   * liscia. Sta QUI e non in `ReliefSpec` perché 'flat-engraved' incide senza avere parete.
   */
  rings: readonly number[];
  /**
   * Raggi quando la faccia porta l'EMBLEMA, che occupa il centro. Sottoinsieme di
   * `rings`: la dama è lo stesso tornio con un medaglione appoggiato sopra, non una
   * faccia incisa diversamente.
   */
  kingRings: readonly number[];
  /**
   * Raggio del bottone tornito al centro della faccia, frazione di `geom.rx`.
   * 0 = lo stile non ne ha.
   */
  boss: number;
  /** Come la dama guadagna volume rispetto alla pedina. */
  kingBody: 'stacked' | 'crown3d' | 'none';
  /**
   * Altezza della PARETE della fascia della corona, frazione di `radius`. Solo
   * `kingBody: 'crown3d'`. NON è l'altezza totale della corona: quella la aggiunge
   * `CROWN_CIRCLET_RISE`, che si misura in R e non in raggi del pezzo.
   */
  crownHeight: number;
  /**
   * Due usi, secondo lo stile: scala dell'emblema sulla faccia (per liberare la fascia
   * dei solchi) sugli stili incisi, e RAGGIO della corona in raggi del pezzo su
   * `kingBody: 'crown3d'`.
   */
  crownScale: number;
}

// =====================================================================
// Color themes
// =====================================================================

/**
 * Le palette pronte, nell'ordine in cui l'elenco delle impostazioni le mostra: prima le
 * quattro VARIANTI, poi le tinte.
 *
 * Le quattro varianti non portano esadecimali propri: sono `variantPieceColors`, cioè
 * esattamente ciò che il tema 'auto' produce quando si sta giocando quella variante. È il
 * punto di averle qui — quei colori prima si potevano avere SOLO giocandoci — e la ragione
 * per cui vanno derivate e non ricopiate: due esadecimali fermi qui e la MasterMatrix
 * ritoccata altrove farebbero divergere in silenzio "Italiana" dall'italiana.
 *
 * Corallo, verde/viola e rosa/blu notte tornano dal ritiro con i valori esatti che
 * avevano, bordi e corone comprese: quelle sono scelte a mano — il corallo porta corone
 * chiare perché l'oro su quell'arancio sparirebbe — e riderivarle le perderebbe.
 * 'cream-black' resta invece fuori: era il classico un'altra volta.
 */
export const PIECE_COLOR_THEMES: Record<Exclude<PieceColorTheme, 'auto' | 'custom'>, ColorPair> = {
  italian: variantPieceColors(GameVariant.ITALIAN),
  international: variantPieceColors(GameVariant.INTERNATIONAL),
  english: variantPieceColors(GameVariant.ENGLISH),
  brazilian: variantPieceColors(GameVariant.BRAZILIAN),
  // Current default — warm wooden cream / dark brown
  classic: {
    white: [
      { offset: '0%', color: '#ffffff' },
      { offset: '35%', color: '#F8F0E0' },
      { offset: '70%', color: '#E5D8C0' },
      { offset: '100%', color: '#D5C5A5' },
    ],
    dark: [
      { offset: '0%', color: '#A08870' },
      { offset: '35%', color: '#7A6050' },
      { offset: '70%', color: '#5D4037' },
      { offset: '100%', color: '#4A3228' },
    ],
    whiteStroke: 'rgba(160,130,90,0.5)',
    darkStroke: 'rgba(40,25,18,0.6)',
    whiteCrown: '#C9A24A',
    darkCrown: '#D4A24C',
    whiteCrownStroke: 'rgba(120,90,40,0.7)',
    darkCrownStroke: 'rgba(30,20,10,0.85)',
  },
  // La dama americana classica: rosso contro nero.
  'red-black': {
    white: [
      { offset: '0%', color: '#FF7A6B' },
      { offset: '35%', color: '#E03A2A' },
      { offset: '70%', color: '#B21F12' },
      { offset: '100%', color: '#7A1008' },
    ],
    dark: [
      { offset: '0%', color: '#5A5A5A' },
      { offset: '35%', color: '#3A3A3A' },
      { offset: '70%', color: '#1F1F1F' },
      { offset: '100%', color: '#0E0E0E' },
    ],
    whiteStroke: 'rgba(90,15,8,0.6)',
    darkStroke: 'rgba(0,0,0,0.7)',
    whiteCrown: '#FFD24A',
    darkCrown: '#E0B85A',
    whiteCrownStroke: 'rgba(90,50,10,0.8)',
    darkCrownStroke: 'rgba(20,15,5,0.85)',
  },
  // Contrasto massimo. Lo stroke del lato chiaro è più marcato del solito perché
  // nelle varianti che giocano su TUTTE le caselle (Turca) un pezzo quasi bianco
  // finisce anche sulla casella chiara, e senza bordo sparirebbe.
  'white-black': {
    white: [
      { offset: '0%', color: '#FFFFFF' },
      { offset: '35%', color: '#FAFAFA' },
      { offset: '70%', color: '#EAEAEA' },
      { offset: '100%', color: '#CFCFCF' },
    ],
    dark: [
      { offset: '0%', color: '#4A4A4A' },
      { offset: '35%', color: '#2A2A2A' },
      { offset: '70%', color: '#141414' },
      { offset: '100%', color: '#050505' },
    ],
    whiteStroke: 'rgba(70,70,70,0.75)',
    darkStroke: 'rgba(0,0,0,0.8)',
    whiteCrown: '#C9A24A',
    darkCrown: '#E8C46A',
    whiteCrownStroke: 'rgba(120,90,40,0.7)',
    darkCrownStroke: 'rgba(20,15,5,0.85)',
  },
  'blue-red': {
    white: [
      { offset: '0%', color: '#7FB6FF' },
      { offset: '35%', color: '#4A88E5' },
      { offset: '70%', color: '#2C5BB8' },
      { offset: '100%', color: '#1A3A7E' },
    ],
    dark: [
      { offset: '0%', color: '#FF8B7F' },
      { offset: '35%', color: '#E54A3A' },
      { offset: '70%', color: '#B82820' },
      { offset: '100%', color: '#7E1410' },
    ],
    whiteStroke: 'rgba(15,30,80,0.6)',
    darkStroke: 'rgba(80,15,10,0.6)',
    whiteCrown: '#FFD24A',
    darkCrown: '#FFE36B',
    whiteCrownStroke: 'rgba(120,90,20,0.8)',
    darkCrownStroke: 'rgba(80,40,0,0.85)',
  },
  // Coppia sicura per il daltonismo (arancio/blu resta distinguibile in deutan e
  // protan, a differenza di blu/rosso e verde/viola). Le corone NON sono entrambe
  // dorate: l'oro sull'arancio sparirebbe, quindi quel lato la porta scura.
  'orange-blue': {
    white: [
      { offset: '0%', color: '#FFC85C' },
      { offset: '35%', color: '#F0A020' },
      { offset: '70%', color: '#C97D0A' },
      { offset: '100%', color: '#8F5600' },
    ],
    dark: [
      { offset: '0%', color: '#5FA8DC' },
      { offset: '35%', color: '#1E7BB8' },
      { offset: '70%', color: '#0A5183' },
      { offset: '100%', color: '#04304F' },
    ],
    whiteStroke: 'rgba(90,55,0,0.6)',
    darkStroke: 'rgba(2,25,45,0.65)',
    whiteCrown: '#7A4600',
    darkCrown: '#FFD24A',
    whiteCrownStroke: 'rgba(50,28,0,0.85)',
    darkCrownStroke: 'rgba(10,35,60,0.85)',
  },
  // Il lato oro è deliberatamente più profondo dell'argento: con un oro chiaro le
  // due luminanze si equivarrebbero e i due colori non si staccherebbero a colpo
  // d'occhio. Per lo stesso motivo la corona sull'oro è chiara, non dorata.
  'silver-gold': {
    white: [
      { offset: '0%', color: '#FFFFFF' },
      { offset: '35%', color: '#E8ECEF' },
      { offset: '70%', color: '#C4CCD2' },
      { offset: '100%', color: '#98A3AC' },
    ],
    dark: [
      { offset: '0%', color: '#E0C060' },
      { offset: '35%', color: '#C09A2E' },
      { offset: '70%', color: '#8C6A15' },
      { offset: '100%', color: '#573F06' },
    ],
    whiteStroke: 'rgba(80,90,98,0.6)',
    darkStroke: 'rgba(45,30,3,0.7)',
    whiteCrown: '#8C7A2E',
    darkCrown: '#FFF3C0',
    whiteCrownStroke: 'rgba(50,42,10,0.8)',
    darkCrownStroke: 'rgba(70,50,5,0.85)',
  },
  // Le corone qui sono CHIARE su entrambi i lati, non dorate: l'oro sul corallo si
  // leggerebbe come oro su oro.
  'coral-teal': {
    white: [
      { offset: '0%', color: '#FFB3A0' },
      { offset: '35%', color: '#FF7F60' },
      { offset: '70%', color: '#D9543A' },
      { offset: '100%', color: '#9C3020' },
    ],
    dark: [
      { offset: '0%', color: '#63CFC9' },
      { offset: '35%', color: '#1FA39C' },
      { offset: '70%', color: '#0E6E69' },
      { offset: '100%', color: '#06423F' },
    ],
    whiteStroke: 'rgba(110,30,18,0.6)',
    darkStroke: 'rgba(3,40,38,0.65)',
    whiteCrown: '#FFE08A',
    darkCrown: '#FFF0B0',
    whiteCrownStroke: 'rgba(120,70,20,0.8)',
    darkCrownStroke: 'rgba(10,60,55,0.85)',
  },
  // NON è una coppia sicura per il daltonismo — verde e viola si confondono in deutan e
  // protan — ed è per questo che 'orange-blue' resta in elenco con la sua etichetta.
  'green-purple': {
    white: [
      { offset: '0%', color: '#A8E6A1' },
      { offset: '35%', color: '#5FBF55' },
      { offset: '70%', color: '#358030' },
      { offset: '100%', color: '#1F4A1C' },
    ],
    dark: [
      { offset: '0%', color: '#C9A8E8' },
      { offset: '35%', color: '#8B5DBF' },
      { offset: '70%', color: '#583585' },
      { offset: '100%', color: '#321E50' },
    ],
    whiteStroke: 'rgba(20,60,15,0.6)',
    darkStroke: 'rgba(40,20,70,0.6)',
    whiteCrown: '#FFD24A',
    darkCrown: '#FFD86B',
    whiteCrownStroke: 'rgba(80,50,10,0.8)',
    darkCrownStroke: 'rgba(60,30,80,0.85)',
  },
  'pink-navy': {
    white: [
      { offset: '0%', color: '#FFC7DC' },
      { offset: '35%', color: '#F58FB4' },
      { offset: '70%', color: '#C95A84' },
      { offset: '100%', color: '#8E3357' },
    ],
    dark: [
      { offset: '0%', color: '#4A5D9E' },
      { offset: '35%', color: '#26356E' },
      { offset: '70%', color: '#131E45' },
      { offset: '100%', color: '#070D22' },
    ],
    whiteStroke: 'rgba(100,30,55,0.6)',
    darkStroke: 'rgba(3,6,18,0.7)',
    whiteCrown: '#FFE08A',
    darkCrown: '#FFD24A',
    whiteCrownStroke: 'rgba(120,70,30,0.8)',
    darkCrownStroke: 'rgba(10,15,40,0.85)',
  },
};

// =====================================================================
// Per-variant colors ('auto' theme) — derive a ColorPair from two base hex
// colors (white piece + black piece) stored in the MasterMatrix.
// =====================================================================

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  const c = (n: number) => clamp255(n).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Rapporto di contrasto WCAG fra due tinte, da 1 (identiche) a 21 (nero su bianco). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Soglia WCAG per gli elementi grafici non testuali. Sotto, l'emblema c'è ma non si
 * stacca: la corona dorata sulla pedina avana dell'italiana stava a 1.05, cioè era
 * oro su oro.
 */
const EMBLEM_MIN_CONTRAST = 3;

/**
 * La tinta dell'emblema della dama, garantita visibile sulla faccia che lo porta.
 *
 * Tiene la tinta dichiarata dal tema finché regge il contrasto — l'oro resta oro
 * sulle pedine scure, e i temi che hanno già scelto a mano (l'arancio/blu porta una
 * corona scura sul lato arancio proprio per questo) non vengono scavalcati. Quando
 * non regge, sceglie fra una versione MOLTO più scura e una molto più chiara della
 * stessa tinta: così cambia il valore ma non la famiglia, e un oro diventa un bronzo
 * profondo invece di un colore estraneo al pezzo.
 *
 * Derivare invece che elencare è obbligatorio, non elegante: i colori 'auto' escono
 * dalla MasterMatrix di venti varianti e quelli 'custom' li sceglie l'utente con uno
 * slider. Nessuna delle due famiglie si può tarare a mano.
 */
// Su una scacchiera esistono al massimo due combinazioni (lato chiaro e lato scuro),
// ma `resolveEmblemColor` gira per ogni pezzo a ogni render: la cache la riduce a due
// calcoli per partita.
const emblemColorCache = new Map<string, string>();

export function resolveEmblemColor(declared: string, face: string): string {
  const key = `${declared}|${face}`;
  const cached = emblemColorCache.get(key);
  if (cached !== undefined) return cached;
  const value = computeEmblemColor(declared, face);
  emblemColorCache.set(key, value);
  return value;
}

function computeEmblemColor(declared: string, face: string): string {
  if (contrastRatio(declared, face) >= EMBLEM_MIN_CONTRAST) return declared;
  const candidates = [
    darken(declared, 0.62),
    lighten(declared, 0.78),
    darken(declared, 0.85),
    lighten(declared, 0.94),
  ];
  let best = declared;
  let bestRatio = contrastRatio(declared, face);
  for (const candidate of candidates) {
    const ratio = contrastRatio(candidate, face);
    // Il primo che supera la soglia vince: i candidati sono in ordine di distanza
    // crescente dalla tinta del tema, quindi ci si allontana il meno possibile.
    if (ratio >= EMBLEM_MIN_CONTRAST) return candidate;
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }
  return best;
}

/**
 * Il tono d'OMBRA della corona: lo stesso metallo, più scuro. SEMPRE più scuro.
 *
 * IL CONTORNO NON PUÒ FARE DA OMBRA, in nessuna delle due direzioni, ed è per questo che
 * questo tono si deriva invece di prenderlo dal tema:
 *
 *  - `emblemStrokeFor` ribalta chiaro/scuro secondo la luminanza: su una dama chiara, dove la
 *    corona è un metallo profondo, restituisce un bordo PALLIDO e semitrasparente — buono per
 *    contornare un emblema scuro, disastroso su un oggetto solido, che diventa di vetro;
 *  - sui pezzi SCURI i temi dichiarano un contorno quasi nero (`rgba(30,20,10,0.85)`), giusto
 *    per contornare un oro. Riempirci il capo scuro del gradiente vuol dire dipingere di nero
 *    la parte di corona più lontana dalla luce, cioè la FASCIA: su fondo scuro spariva.
 *
 * Un metallo ha il lato in ombra scuro comunque sia il pezzo sotto, ma di un'ombra sua.
 */
export function crownShadeFor(tone: string): string {
  return darken(tone, 0.52);
}

/**
 * Il colpo di LUCE sul metallo: la stessa tinta schiarita.
 *
 * La banda di luce va da un tono d'ombra a uno più chiaro della tinta base: fermandosi alla
 * base il metallo resta piatto, e su una dama chiara — dove la tinta ripiega su un tono
 * profondo per reggere il contrasto — legge fango invece che metallo.
 */
export function crownSheenFor(tone: string): string {
  return lighten(tone, 0.42);
}

/** Sopra questa luminanza il bordo dell'emblema si ottiene SCURENDO; sotto, schiarendo. */
const EMBLEM_STROKE_DARKENS_ABOVE = 0.4;

/** Il bordo dell'emblema: scuro se la tinta è chiara, chiaro se è scura. */
export function emblemStrokeFor(fill: string): string {
  return relativeLuminance(fill) > EMBLEM_STROKE_DARKENS_ABOVE
    ? rgba(darken(fill, 0.72), 0.85)
    : rgba(lighten(fill, 0.7), 0.6);
}

/**
 * Il tono della CALOTTA sotto gli archi della corona imperiale: la faccia del pezzo, scurita.
 *
 * È il velluto delle corone vere, senza introdurre un rosso che non viene dalla tavolozza:
 * nasce dal colore della pedina, quindi segue qualunque tema e qualunque colore
 * personalizzato, e stacca dal metallo perché è di un'altra famiglia.
 */
export function crownCapFrom(face: string): string {
  return darken(face, 0.68);
}

/** Mix a color toward white by `amount` (0..1). */
function lighten(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
}

/** Mix a color toward black by `amount` (0..1). */
export function darken(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Build the 4 radial-gradient stops (top highlight → darker edge) from a base color. */
function gradientFromBase(base: string): GradientStop[] {
  return [
    { offset: '0%', color: lighten(base, 0.2) },
    { offset: '35%', color: base },
    { offset: '70%', color: darken(base, 0.14) },
    { offset: '100%', color: darken(base, 0.28) },
  ];
}

/**
 * Derive a full ColorPair (gradients, strokes, crowns) from the two base piece
 * colors of a variant. Crowns keep the shared gold used by the explicit themes.
 */
export function colorPairFromVariant(whiteHex: string, blackHex: string): ColorPair {
  return {
    white: gradientFromBase(whiteHex),
    dark: gradientFromBase(blackHex),
    whiteStroke: rgba(darken(whiteHex, 0.4), 0.55),
    darkStroke: rgba(darken(blackHex, 0.55), 0.65),
    whiteCrown: '#C9A24A',
    darkCrown: '#D4A24C',
    whiteCrownStroke: 'rgba(120,90,40,0.7)',
    darkCrownStroke: 'rgba(30,20,10,0.85)',
  };
}

/** The piece ColorPair derived from a variant's MasterMatrix base colors. */
export function variantPieceColors(variant: GameVariant): ColorPair {
  const c = VARIANT_SPECS[variant].ui.colors;
  return colorPairFromVariant(c.whitePiece, c.blackPiece);
}

/** I due esadecimali scelti a mano dall'utente per un asse di colore. */
export interface CustomColorPair {
  light: string;
  dark: string;
}

/**
 * Resolve the piece colors for a board. With the 'auto' theme (default) the
 * colors come from the active variant's MasterMatrix entry; any explicit theme
 * overrides the per-variant default.
 *
 * Con 'custom' la coppia si deriva dai due esadecimali dell'utente esattamente come
 * si deriva da quelli della variante: stessa scala di gradiente, stessi bordi, stessa
 * corona. Così un colore scelto a mano non è un caso a parte da illuminare, e resta
 * coerente con i materiali e col rilievo.
 */
export function resolvePieceColors(
  theme: PieceColorTheme,
  variant: GameVariant,
  custom?: CustomColorPair,
): ColorPair {
  if (theme === 'auto') return variantPieceColors(variant);
  if (theme === 'custom') {
    // Senza i valori dell'utente (usi che non li passano) si ricade sulla variante,
    // non su una palette fissa: è l'unico ripiego che non stona con la scacchiera.
    return custom ? colorPairFromVariant(custom.light, custom.dark) : variantPieceColors(variant);
  }
  return PIECE_COLOR_THEMES[theme] ?? PIECE_COLOR_THEMES.classic;
}

// =====================================================================
// Materiali (4) — luce e venatura, nessuna geometria
// =====================================================================

export const PIECE_MATERIALS: Record<Material, MaterialSpec> = {
  // Legno tornito: luce diffusa, solchi morbidi, venatura MARCATA — è il materiale
  // che si riconosce dalla venatura, quindi la sua è la più forte delle quattro.
  wood: {
    cx: '38%',
    cy: '32%',
    r: '70%',
    innerLightWhite: ['rgba(255,255,255,0.66)', 'rgba(255,255,255,0)'],
    innerLightDark: ['rgba(255,255,255,0.16)', 'rgba(255,255,255,0)'],
    innerDarkWhite: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.12)'],
    innerDarkDark: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.22)'],
    shadowOpacityWhite: 0.3,
    shadowOpacityDark: 0.4,
    grain: 'wood',
    // TARATA CONTRO IL PICCO NERO DEL FILE, non a occhio: con la riscrittura della venatura
    // il picco e' sceso da 0.587 a 0.510 (misurato, vedi board-materials.ts), quindi 0.32
    // avrebbe reso il pezzo piu' spento a parita' di tutto il resto. 0.32*0.587 = 0.188 di
    // nero effettivo, e 0.188/0.510 = 0.37. A 0.5 il pezzo diventa una tavola.
    grainOpacity: 0.37,
    cut: 'soft',
  },

  // Marmo: luce larga e morbida, vene rade e appena accennate.
  marble: {
    cx: '45%',
    cy: '40%',
    r: '85%',
    innerLightWhite: ['rgba(255,255,255,0.5)', 'rgba(255,255,255,0)'],
    innerLightDark: ['rgba(255,255,255,0.2)', 'rgba(255,255,255,0)'],
    innerDarkWhite: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.08)'],
    innerDarkDark: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.15)'],
    shadowOpacityWhite: 0.25,
    shadowOpacityDark: 0.35,
    grain: 'marble',
    grainOpacity: 0.22,
    cut: 'crisp',
  },

  // Vetro: corpo semitrasparente con nucleo opaco (senza il nucleo il pezzo sparisce
  // sulle caselle scure). Niente venatura: quello che lo identifica è il riflesso.
  glass: {
    cx: '34%',
    cy: '26%',
    r: '78%',
    innerLightWhite: ['rgba(255,255,255,0.85)', 'rgba(255,255,255,0)'],
    innerLightDark: ['rgba(255,255,255,0.5)', 'rgba(255,255,255,0)'],
    innerDarkWhite: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.1)'],
    innerDarkDark: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.18)'],
    shadowOpacityWhite: 0.28,
    shadowOpacityDark: 0.34,
    grain: null,
    grainOpacity: 0,
    cut: 'crisp',
    translucent: true,
  },

  // Plastica da club: highlight speculare stretto e superficie OPACA, senza venatura.
  plastic: {
    cx: '30%',
    cy: '25%',
    r: '60%',
    innerLightWhite: ['rgba(255,255,255,0.95)', 'rgba(255,255,255,0)'],
    innerLightDark: ['rgba(255,255,255,0.42)', 'rgba(255,255,255,0)'],
    innerDarkWhite: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.2)'],
    innerDarkDark: ['rgba(0,0,0,0)', 'rgba(0,0,0,0.32)'],
    shadowOpacityWhite: 0.36,
    shadowOpacityDark: 0.46,
    grain: null,
    grainOpacity: 0,
    cut: 'crisp',
  },
};

// =====================================================================
// Geometrie (5) — cosa si disegna, indipendentemente dal materiale
// =====================================================================

/**
 * Schiacciamento verticale della faccia negli stili 3D: è l'angolo di camera.
 *
 * Sta qui e non in Piece.tsx perché è la costante su cui poggiano gli invarianti
 * dell'incisione: i solchi sono ELLISSI concentriche, e in alto e in basso — dove si
 * avvicinano di più — due contorni distano `Δk · FACE_SQUASH · rx`, non `Δk · rx`.
 */
export const FACE_SQUASH = 0.66;

/**
 * Direzione da cui scivola il labbro illuminato dei solchi: la luce arriva dall'alto e
 * un po' da sinistra, quindi il labbro si sposta in basso a destra.
 */
export const LIGHT_DIR = { x: 0.5, y: 0.866 } as const;

/**
 * Quanto scivola il labbro, in tratti di solco.
 *
 * Più in là il labbro si mangerebbe il legno che deve restare fra due solchi: il
 * vincolo lo detta il ROMBO, che ha `lipFactor 1.3`. Vedi `markLandPx`.
 */
export const LIP_SHIFT_K = 0.4;

/**
 * Bersaglio tornito, come sul pezzo vero: bordo piano largo, solchi a passo uniforme
 * che marciano verso il centro, e al centro il bottone.
 *
 * Il vincolo che detta i valori NON è l'inviluppo isotropo della corona
 * (`hypot(0.45, 0.35) · crownScale` = 0.456) ma il LEGNO NETTO che resta in alto sulla
 * faccia schiacciata: `Δk · S − 0.8 · strokeK − labbro`, con `S` = separazione verticale
 * (0.66 sul tondo, 0.594 sul quadrato, 0.5508 sul ROMBO, che è il caso peggiore perché
 * ha anche `lipFactor 1.3`). Con il vecchio passo 0.16 quel numero valeva −0.45 px al
 * tier `mid`: i due solchi si FONDEVANO. Passo 0.29 ⇒ minimo 1.08 px su ogni forma e
 * ogni tier. Non scendere. Verificato da piece-themes.test.ts.
 */
const ENGRAVED_RINGS: readonly number[] = [0.82, 0.53];

/** Sulla dama l'emblema OCCUPA il centro: resta il solo solco esterno. */
const ENGRAVED_KING_RINGS: readonly number[] = [0.82];

/**
 * Bottone tornito al centro della faccia.
 *
 * Sotto 0.25 il nucleo chiaro che resta dentro il suo scalino scende sotto il pixel
 * alla dimensione minima del tier `mid` (quadrato: 0.25·0.594·11 − 0.105·11/2 = 1.06 px)
 * e il bottone legge come una macchia scura invece che come un rilievo.
 */
const ENGRAVED_BOSS = 0.25;

/**
 * Passo MINIMO della catena di segni concentrici (solchi più bottone): è il numero su
 * cui `maxRingsFor` decide quanti solchi regge una forma a punta.
 */
const MIN_MARK_GAP = Math.min(
  ...ENGRAVED_RINGS.map((k, i) => k - (ENGRAVED_RINGS[i + 1] ?? ENGRAVED_BOSS)),
);

/** Spessore del corpo estruso, condiviso dai tre stili 3D. */
const MAN_HEIGHT = 0.4;
/** Doppio disco: sensibilmente più alto della pedina, o la giuntura è l'unico segnale. */
const STACKED_KING_HEIGHT = 0.84;
/**
 * LA CORONA 3D È UN CERCHIETTO, NON UNA PLACCA.
 *
 * Misure in frazioni di `R`, il raggio della corona (`radius · crownScale · crownFitScale`),
 * con origine nel CENTRO dell'ellisse del bordo superiore della fascia. Il punto del bordo
 * all'angolo θ vale `(R·cosθ, R·squash·sinθ)`: `sinθ > 0` è l'arco ANTERIORE — più in basso
 * a schermo — e `sinθ < 0` quello POSTERIORE, che si vede attraverso l'apertura. La
 * prospettiva viene da sé: le punte dietro si addensano, quelle davanti si aprono, ed è
 * QUESTO — non uno spessore estruso — a far leggere l'anello come un oggetto tondo.
 *
 * `squash` è PIÙ PIATTO di quello della faccia, ed è una deviazione voluta. Alla
 * foreshortening del pezzo (0.66) l'apertura di un anello largo tre quarti scende così in
 * basso davanti che la corona legge come una scodella, con le punte anteriori sospese dentro
 * il buco: provato e scartato. A 0.38 — lo stesso rapporto della corona dell'immagine di
 * riferimento — il cerchietto resta un cerchietto. È il solo punto in cui il disegno della
 * corona non segue la proiezione del corpo, e ci si guadagna la sola cosa che conta: che si
 * riconosca come una corona.
 */
export const CROWN_CIRCLET = {
  squash: 0.38,
  /**
   * Semi-apertura della base della punta, in passi angolari. A 0.5 le basi si toccano e il
   * bordo superiore diventa uno zigzag continuo, come sulla corona vera; sotto, fra una
   * punta e l'altra resterebbe un tratto di bordo piatto che legge come una dentiera.
   */
  halfSpan: 0.5,
  /** Sferetta in cima alla punta. Il centro sta mezzo raggio sopra l'apice, quindi sporge. */
  ballR: 0.13,
  /** Apice degli archi della corona imperiale, sopra il centro del bordo. */
  archApex: 0.74,
  /** Globo in cima agli archi: poggia sull'apice, quindi sale di due raggi. */
  orbR: 0.13,
  /** Crocetta sopra il globo. */
  crossH: 0.2,
  /** Semi-larghezza del nastro dell'arco, al piede e in cima: si rastrema salendo. */
  archWidth: [0.12, 0.045],
} as const;

/**
 * I CINQUE TIPI DI CORONA: le proporzioni di ciascuno, scelte dall'utente con la preferenza
 * `crownType`.
 *
 * Per un giro questo asse è stato `kingIcon`, lo stesso dell'emblema della dama: scegliere
 * "imperiale" sulla corona cambiava l'emblema degli stili piatti in "anello", e viceversa. Ora
 * è una preferenza sua — vedi `CrownType` in shared.
 *
 * `pointH` e `centerBoost` stanno QUI e non nello spec condiviso perché sono la differenza
 * fra un tipo e l'altro: l'imperiale ha le punte basse e nessun rialzo centrale, che al
 * centro ci vanno gli archi.
 */
export interface CrownTypeSpec {
  /** Sagoma della punta: triangolo o giglio a tre lobi. */
  point: 'spike' | 'lily';
  /**
   * Punte equidistanti, con una centrata sul fronte. Il numero NON è libero: gli angoli sono
   * `90° + k · (360/count)`, e un conteggio che ne manda una a 0° o 180° la mette esattamente
   * di FIANCO, dove il bordo è visto di taglio — lì il triangolo si riduce a uno spillo e la
   * perlina sembra un'antenna. Otto facevano così, ed è per questo che sono state scartate.
   * Cinque e dieci tengono la punta più laterale a 18° dal bordo; dodici la rimetterebbero
   * sul bordo.
   */
  count: number;
  /**
   * Cosa porta in cima ogni punta: niente o una perla.
   *
   * Non è un booleano perché la quota d'ingombro deve seguirlo: con `balls: boolean` la corona
   * senza perle restava centrata come quella con le perle, e la dama sbandava nella casella.
   */
  tip: 'none' | 'pearl';
  /**
   * Quanto è grande la perla rispetto a `ballR`. Serve al diadema, che con dieci punte le
   * vuole minute: a grandezza piena si toccherebbero fra loro.
   */
  tipScale: number;
  /** Archi che si chiudono su un globo con la crocetta. */
  arches: boolean;
  /** Altezza della punta sopra il bordo superiore, in R. */
  pointH: number;
  /** Quanto è più alta la punta CENTRALE del fronte, in multipli di `pointH`. */
  centerBoost: number;
}

/**
 * Il rialzo della punta centrale (`centerBoost`) non è un vezzo: senza, le due punte di
 * dietro sono le cose più alte del disegno — il bordo posteriore sta già `2 · squash · R`
 * più su — e fra loro, al centro della cresta, con cinque punte cade un incavo. La sagoma
 * legge a due corni invece che a corona. Una punta centrale più alta è per giunta una cosa
 * vera, il fiorone al centro del cerchio, ed è il profilo delle corone di riferimento:
 * bassa, media, ALTA, media, bassa.
 */
export const CROWN_TYPE_GEOMETRY: Record<CrownType, CrownTypeSpec> = {
  // Essenziale: la corona nuda, senza perle.
  plain: { count: 5, point: 'spike', tip: 'none', tipScale: 1, arches: false, pointH: 0.62, centerBoost: 2 },
  // Classica: le punte portano la perla.
  classic: { count: 5, point: 'spike', tip: 'pearl', tipScale: 1, arches: false, pointH: 0.62, centerBoost: 2 },
  /**
   * Diadema: dieci punte basse e fitte, con perline minute. È la cresta continua a farlo
   * leggere come un ANELLO invece che come una corona, e per questo non ha rialzo al centro:
   * il rialzo serviva a chiudere l'incavo che con cinque punte cade in mezzo alla cresta
   * posteriore, e con dieci lì c'è una punta (270°).
   */
  diadem: { count: 10, point: 'spike', tip: 'pearl', tipScale: 0.62, arches: false, pointH: 0.3, centerBoost: 1 },
  // Gigliata: punte a giglio, senza perle — il fiore sostituisce la sferetta.
  lily: { count: 5, point: 'lily', tip: 'none', tipScale: 1, arches: false, pointH: 0.68, centerBoost: 1.85 },
  // Imperiale: punte basse, e sopra gli archi che si chiudono sul globo. Il centro non si
  // rialza (ci passano gli archi) e le punte si accorciano per fargli spazio sotto il tetto.
  imperial: { count: 5, point: 'spike', tip: 'pearl', tipScale: 1, arches: true, pointH: 0.44, centerBoost: 1 },
};

/**
 * Quanto sporge sopra l'apice della punta ciò che ci sta in cima, in R.
 *
 * Prende lo SPEC e non il solo tipo di punta: il diadema porta perline ridotte, e con la
 * misura piena la dama resterebbe centrata per una corona che non disegna più.
 */
export function crownTipRise(spec: CrownTypeSpec): number {
  return spec.tip === 'pearl' ? 1.5 * CROWN_CIRCLET.ballR * spec.tipScale : 0;
}

/**
 * Quanto sale un tipo di corona sopra il CENTRO del bordo superiore, in R.
 *
 * Il massimo fra le punte — di dietro partono più in alto, la centrale è più alta ma parte
 * più in basso — e, quando ci sono, gli archi col globo e la crocetta. È il numero da cui
 * dipende il centraggio della dama nella casella: piece-themes.test.ts lo verifica per ogni
 * tipo, non solo per il più comune.
 */
export function crownRiseFor(type: CrownType): number {
  const spec = CROWN_TYPE_GEOMETRY[type];
  const { squash, archApex, orbR, crossH } = CROWN_CIRCLET;
  const points =
    Math.max(squash + spec.pointH, spec.pointH * spec.centerBoost - squash) + crownTipRise(spec);
  const arches = spec.arches ? archApex + 2 * orbR + crossH : 0;
  return Math.max(points, arches);
}

/**
 * Lo stesso ingombro, ma della corona con la PROFONDITÀ ROVESCIATA: è quella che si disegna
 * per il riflesso sul vetro, dove il `scale(1, -k)` che ribalta l'immagine rimetterà dritta
 * la profondità e girerà le sole altezze (vedi `rim` in crown3d.tsx).
 *
 * Rovesciare la profondità cambia QUALE punta è la più alta. Nella corona vera la punta
 * centrale del fronte parte in basso sull'ellisse — `pointH · centerBoost − squash` — e a
 * vincere è quella di dietro, che parte in alto; nell'immagine parte in alto e la sua altezza
 * si somma alla profondità invece di sottrarla, quindi vince sempre lei. Il termine di dietro
 * non entra mai nel massimo: `squash + pointH·cb ≥ pointH − squash·|sin t|` per ogni
 * `centerBoost ≥ 1`.
 *
 * E questo numero è ESATTO, non una stima come `crownRiseFor`: la punta centrale sta a
 * `t = π/2` preciso, mentre là il termine `squash + pointH` immagina una punta a 270° che con
 * cinque non c'è.
 */
export function crownMirrorRiseFor(type: CrownType): number {
  const spec = CROWN_TYPE_GEOMETRY[type];
  const { squash, archApex, orbR, crossH } = CROWN_CIRCLET;
  const points = squash + spec.pointH * spec.centerBoost + crownTipRise(spec);
  const arches = spec.arches ? archApex + 2 * orbR + crossH : 0;
  return Math.max(points, arches);
}

/**
 * L'ingombro su cui si TARA il riflesso — che non è quello che si disegna.
 *
 * Nello specchio l'altezza si somma alla profondità, quindi il punto più alto di una corona
 * conta per quel che è PIÙ quel che sta avanti. La punta centrale della classica è alta il
 * doppio delle altre e sta sul fronte: ci guadagna `squash` e arriva a 1.815 R. La crocetta
 * dell'imperiale sta invece sull'ASSE, profondità zero, e resta a 1.20 R. Geometricamente è
 * giusto; visivamente no, perché le due corone si vedono alte uguale — 1.195 R e 1.20 R — e
 * l'imperiale rifletteva un quarto più corta.
 *
 * Alla corona che sale sull'asse si concede quindi la stessa profondità che le punte degli
 * altri tipi prendono dal fronte. È una stilizzazione DICHIARATA, non una misura: per questo
 * sta qui e non dentro `crownMirrorRiseFor`, che deve continuare a dire quanto è alto il
 * disegno vero — c'è un test che glielo chiede. E tocca il solo tipo che ha gli archi.
 */
export function crownMirrorLayoutRise(type: CrownType): number {
  const spec = CROWN_TYPE_GEOMETRY[type];
  const { squash, archApex, orbR, crossH } = CROWN_CIRCLET;
  const points = squash + spec.pointH * spec.centerBoost + crownTipRise(spec);
  const arches = spec.arches ? squash + archApex + 2 * orbR + crossH : 0;
  return Math.max(points, arches);
}

/**
 * Il tipo di corona PIÙ ALTO, in R: è la quota su cui si tara la casella.
 *
 * Il disegno usa `crownRiseFor(tipo)`, che è più basso per i tipi bassi; questo serve dove
 * conta il caso peggiore. Prima lo stesso numero stava scritto a mano in tre file
 * (`CROWN_UNIT_TOP`/`CROWN_UNIT_BOTTOM` in Piece.tsx e `CROWN_UNIT_SPAN` nel test) e nulla
 * impediva che divergessero.
 */
export const CROWN_CIRCLET_RISE = Math.max(...CROWN_TYPES.map(crownRiseFor));

/**
 * Lo stesso caso peggiore per il disegno ROVESCIATO: la corona più alta fra quelle che si
 * specchiano sul vetro, in R.
 *
 * Serve perché la compressione del riflesso è UNA SOLA per tutti i tipi, e la detta lui. La
 * regola di prima — ogni corona compressa quanto basta a riempire la banda — sembrava innocua
 * e non lo era: la banda è una proprietà della SAGOMA e della profondità del riflesso, e non
 * sa niente di quanto sia alta la corona. Il diadema, che è la più bassa di tutte (punte a
 * 0.30 R e nessun rialzo centrale), veniva compresso a 0.70 contro lo 0.41 della classica, e
 * sul cuore arrivava a 1.06 — cioè il riflesso era disegnato più alto della corona vera.
 * Comprimendo tutti dello stesso fattore, una corona bassa riflette corto e una alta lungo.
 */
export const CROWN_MIRROR_RISE = Math.max(...CROWN_TYPES.map(crownMirrorLayoutRise));

/**
 * Altezza della parete della fascia, in raggi del pezzo.
 *
 * Il tetto lo detta la casella: la dama si estende per
 * `crownHeight + CROWN_CIRCLET_RISE · crownScale` sopra il piano d'appoggio e per
 * `manHeight + FACE_SQUASH` sotto, contro i `2 · 1.25` raggi che la casella offre. Con 0.20
 * e `crownScale` 0.78 la semi-altezza vale 1.07 — meno della corona a prisma di prima, che
 * era più piccola ma tutta in altezza — contro il tetto di 1.15 che piece-themes.test.ts
 * tiene: sopra quel valore la dama comincia a invadere la riga di sopra anche prima di
 * essere tagliata.
 */
const CROWN_3D_BAND_HEIGHT = 0.24;
/**
 * Raggio della corona, in raggi del pezzo: larga poco più di tre quarti del pezzo, come
 * quella dell'immagine di riferimento.
 * Il prisma di prima ne occupava 0.40 e si leggeva come un francobollo appoggiato sopra.
 */
const CROWN_3D_SCALE = 0.78;

export const PIECE_STYLE_GEOMETRY: Record<PieceStyle, StyleGeometry> = {
  // Default: corpo estruso, faccia incisa, dama a doppio disco con giuntura.
  '3d-stacked-engraved': {
    relief: { manHeight: MAN_HEIGHT, kingHeight: STACKED_KING_HEIGHT },
    rings: ENGRAVED_RINGS,
    kingRings: ENGRAVED_KING_RINGS,
    boss: ENGRAVED_BOSS,
    kingBody: 'stacked',
    crownHeight: 0,
    crownScale: 0.8,
  },

  // Stesso corpo, faccia liscia: il materiale si legge senza la trama dei solchi.
  '3d-stacked-smooth': {
    relief: { manHeight: MAN_HEIGHT, kingHeight: STACKED_KING_HEIGHT },
    rings: [],
    kingRings: [],
    boss: 0,
    kingBody: 'stacked',
    crownHeight: 0,
    crownScale: 0.9,
  },

  // Il corpo NON cresce: la dama è il pezzo con una corona 3D sopra, come se il disco
  // sotto fosse la testa del re.
  '3d-crown': {
    relief: { manHeight: MAN_HEIGHT, kingHeight: MAN_HEIGHT },
    rings: [],
    kingRings: [],
    boss: 0,
    kingBody: 'crown3d',
    crownHeight: CROWN_3D_BAND_HEIGHT,
    crownScale: CROWN_3D_SCALE,
  },

  // Piatti: nessuno spessore, quindi la dama si distingue solo per l'emblema.
  'flat-plain': {
    rings: [],
    kingRings: [],
    boss: 0,
    kingBody: 'none',
    crownHeight: 0,
    crownScale: 1,
  },

  'flat-engraved': {
    rings: ENGRAVED_RINGS,
    kingRings: ENGRAVED_KING_RINGS,
    boss: ENGRAVED_BOSS,
    kingBody: 'none',
    crownHeight: 0,
    crownScale: 0.8,
  },
};

// =====================================================================
// Relief shades — tinte derivate per parete, solchi e giuntura
// =====================================================================

export interface ReliefShades {
  /**
   * I tre toni del fianco del cilindro, da sinistra a destra: è la banda di luce
   * che curva sul fianco a farlo leggere come una superficie tonda invece che come
   * una fascia piatta di colore.
   */
  wallDark: string;
  wallLight: string;
  wallMid: string;
  /** Bordo della parete, per staccarla dalla casella. */
  wallStroke: string;
  /** Tono scuro del solco inciso. */
  grooveDark: string;
  /** Tono chiaro appena più interno: è il labbro illuminato del solco. */
  grooveLight: string;
  /** Piano del bottone centrale: più alto della terra attorno, quindi prende più luce. */
  bossTop: string;
  /**
   * Giuntura tra i due dischi della dama: l'ombra della fessura, che corre tutt'attorno
   * al fianco.
   *
   * Una riga sola non può funzionare per tutti i colori. Derivata scurendo il bordo,
   * su una pedina già scura diventa nero su nero e la dama torna a sembrare una pedina
   * alta; derivata schiarendo, sparirebbe su quelle chiare. Il rimedio è la COPPIA
   * scuro + chiaro, ma distribuita nello spazio invece che sovrapposta: vedi
   * `seamLight`.
   */
  seam: string;
  /**
   * Il bordino illuminato del disco INFERIORE, acceso solo dove il fianco è in ombra.
   *
   * Prima era un nucleo chiaro sovrapposto al tratto scuro per tutto il giro: la
   * giuntura leggeva bianca ovunque e pesava più della fessura che doveva
   * rappresentare. La luce del pezzo arriva da sinistra — la parete è chiara al 16% e
   * scura al 62% — quindi una riga chiara sulla parte già chiara è invisibile, e su
   * quella scura è tutto ciò che serve. Distribuendola con un gradiente che si accende
   * verso destra si ottengono le due cose insieme: contrasto costante contro la parete
   * e una fessura che sembra tale invece di una fascia dipinta.
   */
  seamLight: string;
}

// La chiave dipende solo da (colore di bordo, tipo di incisione): su una scacchiera
// esistono al massimo 2 combinazioni, quindi la mappa resta di 2 voci.
const reliefShadeCache = new Map<string, ReliefShades>();

/**
 * Deriva le tinte del rilievo dall'ultimo stop del gradiente del pezzo (il colore
 * di bordo), così ogni tema colore — compresi quelli per-variante generati a runtime —
 * ottiene solchi coerenti senza doverli dichiarare.
 */
export function reliefShades(stops: GradientStop[], cut: 'soft' | 'crisp'): ReliefShades {
  const edge = stops[stops.length - 1]?.color ?? '#888888';
  const key = `${edge}|${cut}`;
  const cached = reliefShadeCache.get(key);
  if (cached) return cached;

  const crisp = cut === 'crisp';
  const shades: ReliefShades = {
    wallDark: darken(edge, 0.42),
    wallLight: darken(edge, 0.06),
    wallMid: darken(edge, 0.3),
    wallStroke: rgba(darken(edge, 0.55), 0.5),
    grooveDark: rgba(darken(edge, 0.5), crisp ? 0.6 : 0.38),
    grooveLight: rgba(lighten(edge, 0.65), crisp ? 0.55 : 0.34),
    // Velatura, non tinta piena: il bottone deve leggersi come un piano più alto della
    // faccia, non come un disco appiccicato sopra. Semitrasparente perché ci passano
    // sopra venatura e rampe di luce del pezzo.
    bossTop: rgba(lighten(edge, 0.6), crisp ? 0.4 : 0.26),
    // Più opachi dei solchi: la giuntura è il segnale che distingue una dama da una
    // pedina, non una rifinitura, e deve reggere anche sui temi a basso contrasto.
    // Ma non quanto prima: il chiaro non è più spalmato su tutto il giro, quindi non
    // serve gridare per farsi sentire sopra il proprio stesso tratto scuro.
    seam: rgba(darken(edge, 0.66), 0.78),
    seamLight: rgba(lighten(edge, 0.62), 0.85),
  };
  reliefShadeCache.set(key, shades);
  return shades;
}

// =====================================================================
// Shapes — geometry of the piece body
// =====================================================================

export type ShapeKind = 'circle' | 'ellipse' | 'square' | 'rhombus' | 'polygon';

/** Vertice del contorno unitario: entrambe le coordinate in [-1, 1], y verso il basso. */
export type UnitPoint = readonly [number, number];

export interface ShapeGeom {
  kind: ShapeKind;
  // Half-extent on each axis (used for circle/ellipse, half side for square,
  // and half-diagonal for rhombus)
  rx: number;
  ry: number;
  // Whether to draw a bevel ring (outer thin border with darker shade)
  bevelRing: boolean;
  /**
   * Contorno unitario chiuso, in senso ORARIO sullo schermo (y in giù). Presente
   * solo su `kind: 'polygon'`: tutta la geometria delle forme fantasia — faccia,
   * solchi, parete estrusa, giuntura — deriva da questo unico elenco di vertici.
   */
  points?: readonly UnitPoint[];
  /** Foro centrale passante, frazione di `rx`. Solo la ciambella. */
  hole?: number;
  /**
   * Quanta distanza REALE guadagnano due contorni concentrici per ogni unità di
   * distanza radiale, in frazioni di `rx`. Vale 1 per cerchio e quadrato, 1/√2
   * per il rombo, e crolla sulle punte acute. È questo — non la distanza radiale —
   * che deve superare lo spessore del tratto perché due solchi non si tocchino.
   */
  grooveFactor: number;
  /**
   * Moltiplicatore dell'offset del labbro chiaro del solco. Vale circa
   * `0.9 / grooveFactor`: sulle forme a punta il labbro va allontanato di più,
   * altrimenti il solco se lo mangia.
   */
  lipFactor: number;
  /**
   * Tetto ai solchi concentrici disegnabili. Le forme a punta non reggono i tre
   * anelli condivisi dagli stili 3D (vedi `maxRingsFor`).
   */
  maxRings?: number;
  /**
   * Il contorno campiona una CURVA, non un poligono: va reso con segmenti cubici
   * passanti per i vertici (Catmull-Rom) invece che con spezzate rette.
   *
   * Serve al cuore, l'unica forma che non ha spigoli veri: a 20 campioni le
   * spezzate sui lobi si vedono a occhio nudo già a dimensione di casella, e
   * infittire i campioni sposterebbe solo la soglia — moltiplicando per giunta i
   * nodi della parete, che emette un sub-path per ogni lato. Triangolo e stella
   * NON lo usano: i loro spigoli sono la forma.
   */
  smooth?: boolean;
}

// =====================================================================
// Contorni unitari delle forme fantasia
// =====================================================================

/** Triangolo con la punta in alto, allargato in basso per riempire la casella. */
const TRIANGLE_POINTS: readonly UnitPoint[] = [
  [0, -0.95],
  [0.95, 0.72],
  [-0.95, 0.72],
];

/**
 * Stella a cinque punte con la punta in alto.
 *
 * Il raggio interno è 0.52 e non il classico 0.382 aureo: a 18-26px di diametro
 * — le dimensioni reali su un telefono da 360px — una stella aurea diventa un
 * ragno di punte sottili. A 0.52 il corpo resta pieno e la sagoma si legge.
 */
export const STAR_POINTS: readonly UnitPoint[] = Array.from({ length: 10 }, (_, i) => {
  const angle = (-90 + i * 36) * (Math.PI / 180);
  const r = i % 2 === 0 ? 1 : 0.52;
  return [r * Math.cos(angle), r * Math.sin(angle)] as UnitPoint;
});

/**
 * Cuore con la punta in basso, campionato dalla parametrica classica e
 * normalizzato al riquadro unitario.
 *
 * Il numero di campioni è un compromesso: sotto i ~16 vertici i lobi diventano
 * spigolosi, sopra i ~24 il `d` dei solchi e della parete cresce senza che alle
 * dimensioni reali si veda la differenza.
 */
const HEART_POINTS: readonly UnitPoint[] = (() => {
  const SAMPLES = 20;
  const raw: [number, number][] = [];
  for (let i = 0; i < SAMPLES; i++) {
    const t = (i / SAMPLES) * 2 * Math.PI;
    const x = 16 * Math.sin(t) ** 3;
    // La parametrica ha la y verso l'ALTO: si nega per passare al sistema SVG,
    // così la punta del cuore finisce in basso.
    const y = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    raw.push([x, y]);
  }
  const xs = raw.map((p) => p[0]);
  const ys = raw.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const hx = (maxX - minX) / 2;
  const hy = (maxY - minY) / 2;
  return raw.map(([x, y]) => [(x - cx) / hx, (y - cy) / hy] as UnitPoint);
})();

/** Raggio del foro della ciambella, frazione di `rx`. */
const DONUT_HOLE = 0.38;

/**
 * Profondità APPARENTE della canna della ciambella, frazione di `bodyH`.
 *
 * Geometricamente l'apertura di fondo starebbe a `bodyH` esatti sotto quella di sopra, ma
 * con `DONUT_HOLE` 0.38 e `FACE_SQUASH` 0.66 le due ellissi si sovrappongono per appena
 * `2 · 0.38 · 0.66 − 0.40` = 0.10 raggi: a 360px sono meno di 2px reali, e il foro si legge
 * come spessore invece che come passante. Alzando l'arco del 20% la falce arriva a 0.18
 * raggi — lo spostamento è piccolo, il guadagno no, perché è una LENTE fra due ellissi e
 * cresce molto più che proporzionalmente.
 *
 * Resta una frazione di `bodyH` e non un valore assoluto: sulla dama a doppio disco
 * (`STACKED_KING_HEIGHT` 0.84) 0.84 · 0.80 = 0.672 supera ancora i 0.50 raggi di apertura
 * del foro, quindi le due ellissi restano disgiunte e la dama continua a non farsi
 * attraversare — che è ciò che si vede davvero guardando una ciambella spessa da
 * quell'angolo.
 */
export const DONUT_BORE_DEPTH_K = 0.8;

/**
 * Raggio della fascia con cui la ciambella marca la dama quando l'emblema cadrebbe nel
 * foro. Valore esplicito e non `(1 + DONUT_HOLE) / 2`: a metà dell'anello (0.69) la
 * fascia — larga `2 · strokeK` — invaderebbe il solco a 0.82 (`0.69 + 0.14 > 0.75`).
 * A 0.58 sta fra foro e solco a ogni tier.
 */
export const DONUT_MARK_BAND = 0.58;

/** Distanza dal punto `p` al segmento `a→b`. */
function pointToSegment(p: UnitPoint, a: UnitPoint, b: UnitPoint): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function pointToOutline(p: UnitPoint, poly: readonly UnitPoint[]): number {
  let min = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const d = pointToSegment(p, poly[i]!, poly[(i + 1) % poly.length]!);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Separazione perpendicolare fra due contorni concentrici, per unità di distanza
 * radiale. Misurata, non ricavata da una formula: si campiona fittamente il
 * contorno ridotto del 10% e si prende la distanza minima dal contorno pieno.
 *
 * Serve un valore MISURATO perché le formule analitiche sbagliano proprio dove
 * conta. L'apotema (distanza centro-lato) restituisce 0.52 sulla stella, ma la
 * separazione vera è 0.467, perché il piede della perpendicolare cade fuori dal
 * lato; la distanza dalla RETTA del lato restituisce valori arbitrariamente
 * piccoli sulle curve campionate, dove un lato corto e quasi radiale ha una retta
 * di supporto che passa vicino al centro. Questa misura invece torna esattamente
 * 1 sul quadrato e 1/√2 sul rombo — i due casi noti che la ancorano.
 */
export function contourGapFactor(points: readonly UnitPoint[]): number {
  const SHRINK = 0.1;
  const PER_EDGE = 12;
  let min = Infinity;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    for (let j = 0; j < PER_EDGE; j++) {
      const t = j / PER_EDGE;
      const p: UnitPoint = [
        (a[0] + (b[0] - a[0]) * t) * (1 - SHRINK),
        (a[1] + (b[1] - a[1]) * t) * (1 - SHRINK),
      ];
      const d = pointToOutline(p, points);
      if (d < min) min = d;
    }
  }
  return min / SHRINK;
}

/**
 * Separazione fra due contorni concentrici sulla FACCIA, per unità di distanza
 * radiale, in frazioni di `rx`.
 *
 * `grooveFactor` vale sulla PIANTA del pezzo, ma la faccia degli stili 3D è
 * schiacciata di `squash` e due contorni si avvicinano molto di più dove la curva è
 * orizzontale — ed è lì che si toccano, quindi è là che va misurata la distanza.
 * Sul tondo e sul quadrato il fattore scende da 1 a `squash`; sul rombo da 1/√2 a
 * `squash/√(1 + squash²)`, cioè da 0.707 a 0.551, ed è il caso peggiore di tutti.
 *
 * A `squash = 1` restituisce esattamente `geom.grooveFactor`.
 */
export function faceGapFactor(geom: ShapeGeom, squash: number): number {
  if (geom.points) {
    return contourGapFactor(geom.points.map(([px, py]) => [px, py * squash] as UnitPoint));
  }
  if (geom.kind === 'rhombus') return squash / Math.hypot(1, squash);
  // Tondo, ellisse e quadrato: in alto e in basso la distanza fra due contorni è
  // esattamente quella radiale, schiacciata.
  return squash;
}

/** Il legno che deve restare fra due segni concentrici, in px reali. */
export const MIN_LAND_PX = 1;

/**
 * Il legno netto che resta fra due segni concentrici, in px reali, nel punto in cui si
 * avvicinano di più: in alto e in basso sulla faccia, dove la curva è orizzontale.
 *
 * È QUESTA la misura che conta, e non la distanza radiale sulla pianta. Due cose la
 * schiacciano insieme:
 *
 * - la faccia degli stili 3D è compressa di `squash`, quindi la separazione vera è
 *   quella di `faceGapFactor` (sul rombo 0.551 invece di 0.707);
 * - il labbro illuminato è il contorno TRASLATO lungo la luce, quindi sporge oltre il
 *   proprio solco verso il vicino, tanto più quanto è alto `lipFactor` (1.9 sulle
 *   forme a punta, contro 0.9 sul tondo).
 *
 * Con i raggi vecchi (passo 0.16) questo numero valeva −0.45 px al tier `mid`: i due
 * solchi si FONDEVANO, e nessun test se ne accorgeva perché tutti misuravano sulla
 * pianta del pezzo.
 *
 * @param innerHasLip `false` per il bottone, che è un piano rialzato senza labbro.
 */
export function markLandPx(
  geom: ShapeGeom,
  outerK: number,
  innerK: number,
  strokeK: number,
  radius: number,
  squash: number,
): number {
  return landPx({
    separation: (outerK - innerK) * faceGapFactor(geom, squash) * geom.rx,
    lipFactor: geom.lipFactor,
    stroke: radius * strokeK,
    squash,
  });
}

function landPx(p: {
  separation: number;
  lipFactor: number;
  stroke: number;
  squash: number;
}): number {
  const lipShift = p.stroke * p.lipFactor * LIP_SHIFT_K * LIGHT_DIR.y * p.squash;
  // Il segno esterno arriva verso l'interno con il proprio mezzo tratto, o con il
  // labbro traslato se questo va più in là; quello interno con il proprio mezzo tratto.
  return p.separation - Math.max(p.stroke / 2, lipShift + p.stroke * 0.3) - p.stroke / 2;
}

/**
 * Quanti solchi concentrici la forma regge.
 *
 * Stesso criterio con cui si sono scelti i raggi: fra due segni deve restare
 * `MIN_LAND_PX` di legno, misurato alla dimensione MINIMA del tier che li disegna —
 * il caso peggiore. Niente margine arbitrario: la soglia è la stessa che l'utente
 * vede, e le forme a punta si fermano prima non per il loro `grooveFactor` ma perché
 * il loro labbro, spostato di quasi due tratti, si mangia il legno rimasto.
 *
 * Vale solo per le forme fantasia: le tre storiche conservano il loro numero di
 * anelli (`maxRings` non definito), margini stretti compresi.
 */
function maxRingsFor(points: readonly UnitPoint[], lipFactor: number): number {
  const faceGap = contourGapFactor(points.map(([px, py]) => [px, py * FACE_SQUASH] as UnitPoint));
  // Il tetto non può superare i solchi che il catalogo dichiara.
  for (let n = ENGRAVED_RINGS.length; n > 1; n--) {
    const tiers = LOD_TIERS.filter((tier) => LOD_SPEC[tier].rings >= n);
    // Nessun tier ne disegna n: la forma non deve reggerli comunque.
    if (tiers.length === 0) continue;
    const fits = tiers.every((tier) => {
      // `polygonGeom` usa rx = radius, quindi la separazione in px è `faceGap · radius`.
      const radius = TIER_MIN_DIAMETER_PX[tier] / 2;
      const land = landPx({
        separation: MIN_MARK_GAP * faceGap * radius,
        lipFactor,
        stroke: radius * LOD_SPEC[tier].strokeK,
        squash: FACE_SQUASH,
      });
      return land >= MIN_LAND_PX;
    });
    if (fits) return n;
  }
  return 1;
}

/**
 * Tetto al moltiplicatore del labbro. Sulla punta del cuore — una cuspide vera,
 * dove le due fiancate hanno la stessa tangente — il fattore misurato è 0.12 e
 * `0.9 / 0.12` spingerebbe il labbro a metà del pezzo, dove non è più il labbro
 * di niente. I solchi delle forme fantasia sono comunque ritagliati sul corpo
 * (vedi il clip in Piece.tsx), quindi vicino alle punte non sbordano.
 */
const LIP_FACTOR_CAP = 2;

interface PolygonSpec {
  points: readonly UnitPoint[];
  grooveFactor: number;
  lipFactor: number;
  maxRings: number;
  smooth?: boolean;
}

/**
 * `contourGapFactor` è quadratico nel numero di vertici, e `getShapeGeom` gira a
 * ogni render di ogni pezzo: le derivate della forma si calcolano una volta sola,
 * al caricamento del modulo.
 */
function polygonSpec(points: readonly UnitPoint[], smooth = false): PolygonSpec {
  const gapFactor = contourGapFactor(points);
  const lipFactor = Math.min(0.9 / gapFactor, LIP_FACTOR_CAP);
  return {
    points,
    grooveFactor: gapFactor,
    lipFactor,
    // Il labbro entra nel conto: sulle forme a punta è spostato di quasi due tratti,
    // ed è lui — non la distanza fra i contorni — a decidere quanti solchi ci stanno.
    maxRings: maxRingsFor(points, lipFactor),
    smooth,
  };
}

const POLYGON_SPECS = {
  triangle: polygonSpec(TRIANGLE_POINTS),
  star: polygonSpec(STAR_POINTS),
  // Il cuore è l'unica forma senza spigoli: i suoi vertici campionano una curva.
  heart: polygonSpec(HEART_POINTS, true),
} as const;

function polygonGeom(spec: PolygonSpec, radius: number): ShapeGeom {
  return {
    kind: 'polygon',
    rx: radius,
    ry: radius,
    bevelRing: false,
    points: spec.points,
    grooveFactor: spec.grooveFactor,
    lipFactor: spec.lipFactor,
    maxRings: spec.maxRings,
    smooth: spec.smooth,
  };
}

// =====================================================================
// Adattamento dell'emblema della dama alla forma
// =====================================================================

/**
 * Contorni unitari degli emblemi, in frazioni del raggio dell'emblema stesso.
 * Servono a misurare quanto l'emblema può crescere dentro una forma, quindi
 * descrivono l'INVILUPPO, non il disegno: della corona conta la base, che è più
 * larga del suo zigzag.
 */
export const CROWN_ZIGZAG: readonly UnitPoint[] = [
  [-0.4, 0.15],
  [-0.3, -0.15],
  [-0.15, 0.05],
  [0, -0.25],
  [0.15, 0.05],
  [0.3, -0.15],
  [0.4, 0.15],
];

/** Lo zigzag chiuso dai due spigoli della base: è anche il volume della corona 3D. */
export const CROWN_OUTLINE: readonly UnitPoint[] = [
  ...CROWN_ZIGZAG,
  [0.45, 0.15],
  [0.45, 0.35],
  [-0.45, 0.35],
  [-0.45, 0.15],
];

/** Diamante a taglio brillante: tavola in alto, cintura, punta in basso. */
export const DIAMOND_POINTS: readonly UnitPoint[] = [
  [-0.28, -0.55],
  [0.28, -0.55],
  [0.55, -0.18],
  [0, 0.6],
  [-0.55, -0.18],
];

/** Scala a cui ciascun emblema è disegnato, rispetto al proprio raggio. */
const STAR_ICON_SCALE = 0.62;
const DIAMOND_ICON_SCALE = 0.85;
/** L'anello segue il contorno della forma, quindi il suo inviluppo è un cerchio. */
const RING_ICON_RADIUS = 0.4;

function scaledOutline(points: readonly UnitPoint[], k: number): readonly UnitPoint[] {
  return points.map(([x, y]) => [x * k, y * k] as UnitPoint);
}

const RING_OUTLINE: readonly UnitPoint[] = Array.from({ length: 16 }, (_, i) => {
  const a = (i / 16) * 2 * Math.PI;
  return [RING_ICON_RADIUS * Math.cos(a), RING_ICON_RADIUS * Math.sin(a)] as UnitPoint;
});

export const KING_ICON_OUTLINE: Record<string, readonly UnitPoint[]> = {
  crown: CROWN_OUTLINE,
  star: scaledOutline(STAR_POINTS, STAR_ICON_SCALE),
  diamond: scaledOutline(DIAMOND_POINTS, DIAMOND_ICON_SCALE),
  ring: RING_OUTLINE,
};

/**
 * Distanza dal centro al contorno della forma lungo la direzione `(dx, dy)`.
 *
 * Restituisce il fattore `t` per cui `t·(dx, dy)` cade esattamente sul bordo, quindi
 * un punto `k·(dx, dy)` sta dentro se e solo se `k <= t`.
 */
function boundaryDistanceAlong(geom: ShapeGeom, dx: number, dy: number): number {
  const { rx, ry } = geom;
  if (geom.kind === 'polygon') {
    const pts = geom.points!;
    let best = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = [pts[i]![0] * rx, pts[i]![1] * ry];
      const [bx, by] = [pts[(i + 1) % pts.length]![0] * rx, pts[(i + 1) % pts.length]![1] * ry];
      // t·d = a + u·(b − a), con u nel segmento e t positivo.
      const ex = bx - ax;
      const ey = by - ay;
      const den = dx * ey - dy * ex;
      if (den === 0) continue;
      const t = (ax * ey - ay * ex) / den;
      const u = (ax * dy - ay * dx) / den;
      if (t > 0 && u >= 0 && u <= 1) best = Math.min(best, t);
    }
    return best;
  }
  if (geom.kind === 'rhombus') return 1 / (Math.abs(dx) / rx + Math.abs(dy) / ry);
  // Il quadrato ha gli angoli arrotondati, ma il raggio è 0.12·rx e nessun emblema
  // ci arriva: trattarlo come rettangolo è un'approssimazione che non morde.
  if (geom.kind === 'square') return 1 / Math.max(Math.abs(dx) / rx, Math.abs(dy) / ry);
  return 1 / Math.hypot(dx / rx, dy / ry);
}

/**
 * Quanto può essere grande l'emblema — e a che quota va messo — per restare dentro
 * la forma.
 *
 * Serve perché l'emblema è scalato su `radius` mentre la forma no: le forme a punta
 * rientrano ben prima del raggio, e sugli stili 3D la faccia è per giunta schiacciata
 * in verticale mentre l'emblema resta tondo. Il risultato era una stella che usciva
 * dai fianchi del triangolo e una gemma con la punta fuori dal cuore.
 *
 * È MISURATO e non ricavato da una formula, come `contourGapFactor`: ogni vertice
 * dell'inviluppo si proietta sul contorno lungo la propria direzione, e vince il più
 * stretto.
 *
 * Se centrato ci sta già tutto (`scale >= 1`) non si tocca NIENTE: tondo, quadrato e
 * ciambella conservano esattamente il rendering di prima. Solo dove l'emblema
 * sborderebbe si cerca anche la quota migliore, perché su un triangolo la parte
 * larga sta in basso e restare al centro costringerebbe a rimpicciolire molto di più
 * di quanto serva.
 *
 * Non dipende dal raggio — tutto scala insieme — quindi si calcola una volta per
 * combinazione e si mette in cache.
 */
export interface IconFit {
  /** Raggio massimo dell'emblema, in frazioni di `radius`. */
  scale: number;
  /** Spostamento verticale, in frazioni del raggio dell'emblema. */
  offsetY: number;
}

const iconFitCache = new Map<string, IconFit>();

const NO_ADJUST: IconFit = { scale: 1, offsetY: 0 };

export function iconFit(shape: PieceShape, icon: string, squash: number): IconFit {
  const outline = KING_ICON_OUTLINE[icon];
  if (!outline) return NO_ADJUST;
  const key = `${shape}|${icon}|${squash}`;
  const cached = iconFitCache.get(key);
  if (cached) return cached;

  const base = getShapeGeom(shape, 1);
  const geom: ShapeGeom = { ...base, ry: base.ry * squash };

  const fitAt = (offsetY: number): number => {
    let fit = Infinity;
    for (const [px, py] of outline) {
      const y = py + offsetY;
      if (px === 0 && y === 0) continue;
      fit = Math.min(fit, boundaryDistanceAlong(geom, px, y));
    }
    // Un filo di margine: un emblema che tocca il contorno si confonde col bordo del
    // pezzo, e sulle forme a punta l'antialiasing lo fa sembrare comunque fuori.
    return Number.isFinite(fit) ? fit * 0.94 : 1;
  };

  const centred = fitAt(0);
  let result: IconFit;
  if (centred >= 1) {
    result = NO_ADJUST;
  } else {
    let best = { scale: centred, offsetY: 0 };
    for (let o = -0.4; o <= 0.4001; o += 0.02) {
      const scale = fitAt(o);
      if (scale > best.scale) best = { scale, offsetY: o };
    }
    // Oltre 1 non si va: `iconR` resta il tetto, questo è solo il vincolo di forma.
    result = { scale: Math.min(best.scale, 1), offsetY: best.offsetY };
  }
  iconFitCache.set(key, result);
  return result;
}

/** Campioni sull'ellisse del bordo: 24 bastano, il contorno più spigoloso è un triangolo. */
const CROWN_FIT_SAMPLES = 24;
const crownFitCache = new Map<PieceShape, number>();

/**
 * Quanto la corona 3D deve rimpicciolirsi per stare DENTRO la faccia della forma.
 *
 * Finché la corona era un prisma largo 0.40 raggi stava dentro qualunque sagoma e il fit non
 * serviva — `Piece.tsx` lo saltava apposta. Il cerchietto è largo tre quarti del pezzo, e su
 * triangolo, stella e cuore la faccia rientra ben prima del raggio: senza questo fattore la
 * fascia esce dai fianchi.
 *
 * Si campiona l'ellisse del bordo INFERIORE, quella che poggia sulla faccia: è il contorno
 * della corona nel piano dove un contorno da rispettare esiste davvero. Le punte e le
 * sferette stanno più in alto, sopra il pezzo, dove non c'è sagoma che le contenga.
 */
export function crownFitScale(shape: PieceShape): number {
  const cached = crownFitCache.get(shape);
  if (cached !== undefined) return cached;

  const base = getShapeGeom(shape, 1);
  const geom: ShapeGeom = { ...base, ry: base.ry * FACE_SQUASH };
  let fit = Infinity;
  for (let i = 0; i < CROWN_FIT_SAMPLES; i++) {
    const t = (i / CROWN_FIT_SAMPLES) * 2 * Math.PI;
    fit = Math.min(fit, boundaryDistanceAlong(geom, Math.cos(t), CROWN_CIRCLET.squash * Math.sin(t)));
  }
  // Come in `iconFit`: sopra 1 non si cresce — il tetto è `crownScale` — e quando si
  // rimpicciolisce si lascia un filo di margine, o il bordo della corona si confonde con
  // quello del pezzo e l'antialiasing la fa sembrare comunque fuori.
  // La tolleranza non è pignoleria: sul tondo il campione CADE sul contorno e il conto
  // torna 0.99999…, che senza epsilon rimpicciolirebbe del 6% la corona proprio nel caso
  // in cui non c'è niente da correggere.
  const result = !Number.isFinite(fit) || fit >= 1 - 1e-9 ? 1 : fit * 0.94;
  crownFitCache.set(shape, result);
  return result;
}

// =====================================================================
// Il bordo INFERIORE della sagoma
// =====================================================================

/**
 * Segmento cubico che porta da `p0` a `p1` passando per la curva di Catmull-Rom
 * definita dai due vertici vicini. I punti di controllo stanno a un sesto della
 * corda fra i vertici scavalcati: è la conversione uniforme standard, e ha la
 * proprietà che serve qui — la curva PASSA per i vertici campionati, quindi la
 * sagoma liscia e il poligono su cui sono misurati solchi e distanze restano
 * ancorati negli stessi punti.
 *
 * Sta QUI e non in Piece.tsx, che è il suo committente principale, perché il
 * bordo inferiore delle forme lisce va misurato sulla CURVA e non sui vertici:
 * due implementazioni della stessa regola si sarebbero separate al primo ritocco,
 * e il riflesso avrebbe smesso di combaciare con la sagoma che riflette.
 */
export interface CubicSegment {
  p0: [number, number];
  c1: [number, number];
  c2: [number, number];
  p1: [number, number];
}

export function catmullRomSegments(pts: readonly [number, number][]): CubicSegment[] {
  const n = pts.length;
  const segs: CubicSegment[] = [];
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n]!;
    const p0 = pts[i]!;
    const p1 = pts[(i + 1) % n]!;
    const next = pts[(i + 2) % n]!;
    segs.push({
      p0,
      c1: [p0[0] + (p1[0] - prev[0]) / 6, p0[1] + (p1[1] - prev[1]) / 6],
      c2: [p1[0] - (next[0] - p0[0]) / 6, p1[1] - (next[1] - p0[1]) / 6],
      p1,
    });
  }
  return segs;
}

function cubicAt(s: CubicSegment, t: number): UnitPoint {
  const u = 1 - t;
  const [a, b, c, d] = [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
  return [
    a * s.p0[0] + b * s.c1[0] + c * s.c2[0] + d * s.p1[0],
    a * s.p0[1] + b * s.c1[1] + c * s.c2[1] + d * s.p1[1],
  ];
}

/**
 * Campioni per segmento sulle forme lisce. Otto: il cuore ne ha venti di segmenti,
 * e il bordo serve a piazzare una banda di riflesso, non a disegnarla.
 */
const SMOOTH_EDGE_SAMPLES = 8;

const polylineCache = new Map<PieceShape, readonly UnitPoint[] | null>();

/**
 * La sagoma come spezzata unitaria, o `null` per le famiglie che hanno una formula
 * chiusa (cerchio, quadrato, rombo, ciambella) e non hanno bisogno di campioni.
 */
function shapePolyline(shape: PieceShape): readonly UnitPoint[] | null {
  const cached = polylineCache.get(shape);
  if (cached !== undefined) return cached;
  const geom = getShapeGeom(shape, 1);
  let poly: readonly UnitPoint[] | null = null;
  if (geom.kind === 'polygon') {
    const pts = geom.points!.map(([px, py]) => [px, py] as [number, number]);
    if (geom.smooth) {
      const out: UnitPoint[] = [];
      for (const seg of catmullRomSegments(pts)) {
        for (let j = 0; j < SMOOTH_EDGE_SAMPLES; j++) out.push(cubicAt(seg, j / SMOOTH_EDGE_SAMPLES));
      }
      poly = out;
    } else {
      poly = geom.points!;
    }
  }
  polylineCache.set(shape, poly);
  return poly;
}

/** Il bordo inferiore della spezzata alla `x` data, in frazioni di `ry`. */
function lowerEdgeAt(poly: readonly UnitPoint[], x: number): number {
  let best = -Infinity;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i]!;
    const [x2, y2] = poly[(i + 1) % poly.length]!;
    if (x1 === x2) {
      if (Math.abs(x - x1) <= 1e-9) best = Math.max(best, y1, y2);
      continue;
    }
    if (x < Math.min(x1, x2) - 1e-9 || x > Math.max(x1, x2) + 1e-9) continue;
    best = Math.max(best, y1 + ((y2 - y1) * (x - x1)) / (x2 - x1));
  }
  return best;
}

const bottomKCache = new Map<PieceShape, number>();

/**
 * Quanto scende il punto più BASSO della sagoma, in frazioni di `ry`.
 *
 * Non è sempre 1, ed è l'assunzione da cui nasceva il riflesso storto: la stella
 * arriva a 0.809 — i suoi vertici bassi stanno a 234° e 306°, non a 270° — e il
 * triangolo a 0.72. Sul riquadro ci arrivano solo tondo, quadrato, rombo,
 * ciambella e cuore, che ha la cuspide proprio in fondo.
 */
export function shapeBottomK(shape: PieceShape): number {
  const cached = bottomKCache.get(shape);
  if (cached !== undefined) return cached;
  const poly = shapePolyline(shape);
  const result = poly ? Math.max(...poly.map((p) => p[1])) : 1;
  bottomKCache.set(shape, result);
  return result;
}

const lowerEdgeMinCache = new Map<string, number>();

/**
 * Quanto scende MENO il bordo inferiore, in frazioni di `ry`, dentro la fascia
 * `|x| ≤ halfSpan` — con `halfSpan` in frazioni di `rx`.
 *
 * Serve a far cominciare il riflesso della corona dove il riflesso del CORPO
 * finisce più in alto: da lì in giù la corona si vede tutta, e dove il corpo
 * scende più a fondo è il ritaglio a scavarla seguendo la curva. Sul cuore i due
 * numeri sono lontanissimi — 0.15 sotto i lobi contro 1 sulla cuspide — ed è
 * esattamente la differenza che una banda orizzontale non sapeva descrivere.
 */
export function lowerEdgeMinK(shape: PieceShape, halfSpan: number): number {
  const span = Math.min(1, Math.max(0, halfSpan));
  const key = `${shape}|${span}`;
  const cached = lowerEdgeMinCache.get(key);
  if (cached !== undefined) return cached;

  const poly = shapePolyline(shape);
  let result: number;
  if (!poly) {
    const kind = getShapeGeom(shape, 1).kind;
    // Il quadrato ha gli angoli arrotondati di 0.12·rx, ma nessuna corona ci arriva:
    // stessa approssimazione che fa già `boundaryDistanceAlong`.
    result = kind === 'rhombus' ? 1 - span : kind === 'square' ? 1 : Math.sqrt(Math.max(0, 1 - span * span));
  } else {
    // Su una spezzata il bordo è lineare a tratti in `x`: il minimo cade su un
    // vertice interno alla fascia o su uno dei due estremi, e campionare a passo
    // fisso lo mancherebbe proprio sulle punte.
    const xs = [-span, span, ...poly.map((p) => p[0]).filter((px) => px > -span && px < span)];
    result = Infinity;
    for (const px of xs) {
      const y = lowerEdgeAt(poly, px);
      if (Number.isFinite(y)) result = Math.min(result, y);
    }
    if (!Number.isFinite(result)) result = shapeBottomK(shape);
  }

  lowerEdgeMinCache.set(key, result);
  return result;
}

export function getShapeGeom(shape: PieceShape, radius: number): ShapeGeom {
  switch (shape) {
    case 'square':
      // Square slightly inset so corners don't touch the cell edges
      return {
        kind: 'square',
        rx: radius * 0.9,
        ry: radius * 0.9,
        bevelRing: false,
        grooveFactor: 1,
        lipFactor: 0.9,
      };
    case 'rhombus':
      // Square rotated 45° — vertices reach the cell edge
      return {
        kind: 'rhombus',
        rx: radius,
        ry: radius,
        bevelRing: false,
        grooveFactor: 1 / Math.SQRT2,
        // Letterale storico: 0.9 / (1/√2) darebbe 1.27, e cambiare il valore
        // sposterebbe il labbro dei solchi su ogni rombo 3D già renderizzato.
        lipFactor: 1.3,
      };
    case 'triangle':
      return polygonGeom(POLYGON_SPECS.triangle, radius);
    case 'star':
      return polygonGeom(POLYGON_SPECS.star, radius);
    case 'heart':
      return polygonGeom(POLYGON_SPECS.heart, radius);
    case 'donut':
      // Cerchio bucato: riusa gli archi del tondo, il foro è ritagliato in evenodd.
      return {
        kind: 'circle',
        rx: radius,
        ry: radius,
        bevelRing: false,
        hole: DONUT_HOLE,
        grooveFactor: 1,
        lipFactor: 0.9,
        /**
         * Un solo solco, e non per un problema di spaziatura come sulle forme a
         * punta: il corpo della ciambella è l'anello fra 0.38 e 1, e con tre solchi
         * concentrici (fino a 0.56) non resta un millimetro per l'emblema della
         * dama — che infatti ripiegava su una fascia identica per tutte le icone,
         * cioè l'impostazione non faceva niente. Con un solco solo a 0.88 il centro
         * resta libero e ogni emblema si disegna per intero.
         */
        maxRings: 1,
      };
    case 'round':
    default:
      return {
        kind: 'circle',
        rx: radius,
        ry: radius,
        bevelRing: false,
        grooveFactor: 1,
        lipFactor: 0.9,
      };
  }
}
